/**
 * Filters and updates an array of upsell items based on the cart state.
 *
 * @param {Array} upsells - Array of upsell items. Each item should have an 'id' and 'maxQuantityAccessible'.
 * @param {Object} cartState - The current state of the cart, should contain an 'items' array.
 * @returns {Array} - Array of items to update. Items not in cartState.items or with different quantities are included.
 */
export const getItemsToUpdate = (upsells, cartState) => {

  const closedUpsellArr = JSON.parse(
    sessionStorage.getItem('ups_items') || '[]',
  );

  const removedAutoAddThresholdItemId = sessionStorage.getItem('auto_add_threshold_removed') ? parseInt(sessionStorage.getItem('auto_add_threshold_removed'), 10) : ''
  const removedAutoAddProdTypeItemId = sessionStorage.getItem('auto_add_product_type_removed') ? parseInt(sessionStorage.getItem('auto_add_product_type_removed'), 10) : ''

  // Reset removed items when cart is empty
  if (cartState?.items?.length === 0) {
    sessionStorage.setItem('auto_add_threshold_removed', '');
    sessionStorage.setItem('auto_add_product_type_removed', '');
  }

  return upsells
    .filter(({
      id,
      maxQuantityAccessible,
      type,
      showQuantitySelector,
      automaticallyDelete,
      productTypesRequired,
      requiresExactQtyToTypeMatch,
      qtyRequired
    }) => {

      const isManuallyRemoved = id === removedAutoAddThresholdItemId || id === removedAutoAddProdTypeItemId

      if (!automaticallyDelete || isManuallyRemoved) return false;

      const cartItem = cartState.items?.find((item) => item.id === id);

      if (type.includes('carousel')) {
        return cartItem?.quantity > maxQuantityAccessible;
      }

      if (type.includes('threshold') && cartItem?.quantity > maxQuantityAccessible) {
        // threshold qty is controlled by Shopify script
        return false
      }

      if (type.includes('product-type') && requiresExactQtyToTypeMatch) {
        const typesInCart = cartState.items
          .filter(item => productTypesRequired.includes(item.product_type.toLowerCase()))
          .reduce((total, item) => total + item.quantity, 0);

        if (parseInt(qtyRequired, 10) !== typesInCart) return false
      }

      return (
        (!cartItem && maxQuantityAccessible > 0 && !closedUpsellArr.includes(id * 0.22)) ||
        (cartItem &&
          !showQuantitySelector &&
          cartItem.quantity !== maxQuantityAccessible &&
          !closedUpsellArr.includes(id * 0.22)) ||
        (cartItem &&
          showQuantitySelector &&
          cartItem.quantity > maxQuantityAccessible &&
          !closedUpsellArr.includes(id * 0.22))
      );
    })
    .map(({ maxQuantityAccessible, ...rest }) => ({
      ...rest,
      quantity: maxQuantityAccessible,
    }));
}

/**
 * Calculates the maximum quantity accessible for a given upsell promotion.
 *
 * @param {Object} upsellPromo - The upsell promotion object containing various properties like 'type', 'threshold', etc.
 * @param {Object} cartState - The current cart state object, should contain an 'items' array with cart items.
 * @param {Array} excludeVariants - An array of variant IDs that should be excluded from the calculation.
 * @returns {number} - The maximum accessible quantity based on the promotion rules.
 */

export const calculateMaxQuantityAccessible = (
  upsellPromo,
  cartState,
  excludeVariants) => {
  if (upsellPromo.type.includes('threshold')) {
    const threshold = parseInt(upsellPromo.threshold, 10) * 100;

    // Calculate total price for items in cart, which are not in exclusion lists ("amount" is accumulator)
    const calculatedTotalPrice = cartState.items.reduce((amount, item) => {

      const productType = item.product_type.toLowerCase();
      let typesToExclude;
      
      // the "product_exclusions_by_type" list comes from the Theme Customizer - "Auto-add (threshold)"
      if (!upsellPromo.product_exclusions_by_type?.includes(',')) {
        typesToExclude = upsellPromo.product_exclusions_by_type?.toLowerCase().trim() || '';
      }
      else {
        const typesToExcludeArray = upsellPromo.product_exclusions_by_type.split(',');
        typesToExclude = typesToExcludeArray.map(type => type.toLowerCase().trim());
      }

      // the "excludeVariants" list comes from upsells.js
      if (excludeVariants.includes(item.id) || typesToExclude.includes(productType)) {
        return amount;
      }
      return item.final_line_price + amount;
    }, 0);

    const gwpQtyPerThreshold = parseInt(upsellPromo.gwp_qty_to_add, 10); // qty to add each time threshold met (from Theme Customizer)
    let thresholdMet = Math.floor(calculatedTotalPrice / threshold); // number of times threshold met
    if (upsellPromo.gwp_max_uses && upsellPromo.gwp_max_uses > 0) {
      thresholdMet = Math.min(thresholdMet, upsellPromo.gwp_max_uses);
    }
    const qtyToAdd = thresholdMet * gwpQtyPerThreshold;

    return qtyToAdd;
  }

  if (upsellPromo.type.includes('product-type')) {

    // Count items of a specific type
    const typesInCart = cartState.items
      .filter(item => upsellPromo.product_types_required.includes(item.product_type.toLowerCase()))
      .reduce((total, item) => total + item.quantity, 0);

    const quantityRequired = parseInt(upsellPromo.quantity_required, 10);

    return Math.floor(typesInCart / quantityRequired);
  }

  return 0;
};

